import { components } from "@/api-schema";
import { UserTable } from "@/components/data-table/predefined/user-table";
import { userTableDefaultColumns } from "@/components/data-table/predefined/user-table-columns";
import { DataTableHeightMode, TableBackground } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { DepartmentFilter } from "@/components/filter/predefined/department-filter";
import { Card, CardContent } from "@/components/ui/card";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Textarea } from "@/components/ui/textarea";
import { UserItem } from "@/components/user-item/user-item";
import i18next from "i18next";
import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { Control, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { z } from "zod";

type User = components["schemas"]["User"];

export const TemporaryPermissionFormSchema = z.object({
  request_for: z.string().nonempty({ message: i18next.t("ram:PleaseSelectAUser") }),
  valid_from: z.date({
    message: i18next.t("ram:PleaseSelectAStartDate"),
  }),
  valid_until: z.date({
    message: i18next.t("ram:PleaseSelectAnEndDate"),
  }),
  reason: z.string().nonempty({ message: i18next.t("ram:PleaseJustifyYourRequest") }),
});

export type TemporaryPermissionFormSchemaType = z.infer<typeof TemporaryPermissionFormSchema>;

type TemporaryPermissionFormFieldsProps = {
  isLoading: boolean;
  formControl: Control<TemporaryPermissionFormSchemaType>;
  currentUserUuid?: string;
};

export function TemporaryPermissionFormFields({
  isLoading,
  formControl,
  currentUserUuid,
}: TemporaryPermissionFormFieldsProps) {
  const { t } = useTranslation(["ram", "common"]);
  const { setValue, getValues } = useFormContext();
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

  const [selectedUser, setSelectedUser] = useState<User>();

  const filters = useMemo(
    () => [new DepartmentFilter(), new CompanyFilter(), new BranchFilter(), new BrandFilter()],
    [],
  );

  const filterValues = useMemo(
    () => ({
      departments: selectedDepartments,
      companies: selectedCompanies,
      branches: selectedBranches,
      brands: selectedBrands,
    }),
    [selectedDepartments, selectedCompanies, selectedBranches, selectedBrands],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    switch (filterId) {
      case "departments":
        setSelectedDepartments(values);
        break;
      case "companies":
        setSelectedCompanies(values);
        break;
      case "branches":
        setSelectedBranches(values);
        break;
      case "brands":
        setSelectedBrands(values);
        break;
    }
  }, []);

  const handleSelectionChange = useCallback(
    (selection: string[] | User[]) => {
      const user = selection[0];
      if (typeof user === "string") {
        throw new Error("Expected user objects from the user table");
      }
      const nextUser = currentUserUuid && user?.uuid !== currentUserUuid ? user : undefined;
      setSelectedUser(nextUser);
      setValue("request_for", nextUser?.uuid ?? "", { shouldValidate: true });
    },
    [setValue, currentUserUuid],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedUser(undefined);
    setValue("request_for", "", { shouldValidate: true });
  }, [setValue]);

  const handleRowClick = useCallback(
    (user: User) => {
      if (!currentUserUuid || user.uuid === currentUserUuid) return;
      const nextUser = selectedUser?.uuid === user.uuid ? undefined : user;
      setSelectedUser(nextUser);
      setValue("request_for", nextUser?.uuid ?? "", { shouldValidate: true });
    },
    [setValue, currentUserUuid, selectedUser],
  );

  const [_, navigate] = useLocation();
  const [dateRangeSelected, setDateRangeSelected] = useState<DateRange | undefined>(() => ({
    from: getValues("valid_from"),
    to: getValues("valid_until"),
  }));

  return (
    <>
      <Card>
        <CardContent className="space-y-6">
          <FormField
            control={formControl}
            name="request_for"
            render={() => (
              <FormItem>
                <FormLabel className="font-semibold">{t("ram:PermissionsFromUser")}</FormLabel>
                <FormDescription>{t("ram:FromWhichPersonWouldYouLikeToReceivePermissions")}</FormDescription>
                <UserTable
                  tableBackground={TableBackground.Card}
                  body={{
                    ...(currentUserUuid && { excludeUuids: [currentUserUuid] }),
                    ...(selectedCompanies.length > 0 && {
                      companies: selectedCompanies,
                    }),
                    ...(selectedBranches.length > 0 && {
                      branches: selectedBranches,
                    }),
                    ...(selectedDepartments.length > 0 && {
                      departments: selectedDepartments,
                    }),
                    ...(selectedBrands.length > 0 && {
                      brands: selectedBrands,
                    }),
                  }}
                  columns={[userTableDefaultColumns.columnAvatar, userTableDefaultColumns.columnName]}
                  features={{
                    multiSelection: false,
                    pagination: true,
                    search: true,
                  }}
                  pageSize={5}
                  searchPlaceholder={t("ram:SearchForUser")}
                  selection={selectedUser ? [selectedUser] : []}
                  selectionMode="object"
                  showClearSelection={false}
                  onSelectionChange={handleSelectionChange}
                  onRowClick={handleRowClick}
                  footerComponent={
                    <div className="bg-muted/30 flex min-h-20 items-center gap-3 rounded-lg p-3">
                      {selectedUser ? (
                        <>
                          <UserItem user={selectedUser} showEmail disableLink className="min-w-0 flex-1" />
                          <Button
                            type="button"
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Secondary}
                            size={ButtonSize.Sm}
                            onClick={handleClearSelection}
                          >
                            {t("common:ClearSelection")}
                          </Button>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">{t("common:SelectUser")}</span>
                      )}
                    </div>
                  }
                  filters={filters}
                  filterValues={filterValues}
                  onFilterChange={handleFilterChange}
                  heightMode={DataTableHeightMode.Content}
                  autoFetch={!!currentUserUuid}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex w-full flex-col space-y-8 space-x-0 md:flex-row md:space-y-0 md:space-x-8">
            <FormItem className="flex w-full flex-col md:w-1/3">
              <FormLabel className="font-semibold">{t("ram:Period")}</FormLabel>
              <FormControl>
                <DateRangePicker
                  selected={dateRangeSelected}
                  onSelect={(range) => {
                    setDateRangeSelected(range);
                    setValue("valid_from", range?.from);
                    setValue("valid_until", range?.to);
                  }}
                />
              </FormControl>
              <FormDescription>{t("ram:InWhatPeriodShouldThePermissionsApply")}</FormDescription>
              <FormMessage />
            </FormItem>

            <FormField
              control={formControl}
              name="reason"
              render={({ field }) => (
                <FormItem className="w-full md:w-2/3">
                  <FormLabel className="font-semibold">{t("ram:Reason")}</FormLabel>
                  <FormControl>
                    <Textarea className="min-h-50" {...field} />
                  </FormControl>
                  <FormDescription>{t("ram:WhyDoYouNeedTemporaryPermissions")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <FormCardActions
        cancelLabel={t("common:Cancel")}
        onCancel={() => navigate("~/ram/temporary/requests")}
        isPending={isLoading}
        submitLabel={t("ram:Request")}
      />
    </>
  );
}
